// The JSON bridge to the Rust engine (crates/engine-wasm). No generated glue:
// strings go in through sbl_alloc, results come back from the output buffer.

import type { Action, Catalog, Frame, HintView, Layout, LogEntry, RecordOutcome, SessionConfig } from "./types";

interface Exports {
  memory: WebAssembly.Memory;
  sbl_alloc(len: number): number;
  sbl_free(ptr: number, len: number): void;
  sbl_out_ptr(): number;
  sbl_out_len(): number;
  sbl_session_new(ptr: number, len: number): number;
  sbl_session_free(handle: number): void;
  sbl_apply(handle: number, ptr: number, len: number, atMs: number): void;
  sbl_record(handle: number, ptr: number, len: number, atMs: number): void;
  sbl_step(handle: number, dt: number): void;
  sbl_settle(handle: number): void;
  sbl_view(handle: number): void;
  sbl_layout(handle: number): void;
  sbl_log(handle: number): void;
  sbl_engine_trace(ptr: number, len: number): void;
  sbl_activity_new(ptr: number, len: number): number;
  sbl_catalog(): void;
  sbl_record_reading(handle: number, atMs: number): void;
  sbl_record_scope(handle: number, atMs: number): void;
  sbl_predict(handle: number, ptr: number, len: number, atMs: number): void;
  sbl_hint(handle: number, atMs: number): void;
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export class Engine {
  private constructor(private readonly ex: Exports) {}

  static async load(url: string): Promise<Engine> {
    const { instance } = await WebAssembly.instantiateStreaming(fetch(url), {});
    return new Engine(instance.exports as unknown as Exports);
  }

  private put(text: string): [number, number] {
    const bytes = encoder.encode(text);
    const ptr = this.ex.sbl_alloc(bytes.length);
    new Uint8Array(this.ex.memory.buffer, ptr, bytes.length).set(bytes);
    return [ptr, bytes.length];
  }

  private out(): string {
    const ptr = this.ex.sbl_out_ptr();
    const len = this.ex.sbl_out_len();
    return decoder.decode(new Uint8Array(this.ex.memory.buffer, ptr, len));
  }

  private outJson<T>(): T {
    return JSON.parse(this.out()) as T;
  }

  newSession(config: SessionConfig): number {
    const [p, n] = this.put(JSON.stringify(config));
    const h = this.ex.sbl_session_new(p, n);
    if (h < 0) throw new Error(this.outJson<{ error: string }>().error);
    return h;
  }

  freeSession(h: number): void {
    this.ex.sbl_session_free(h);
  }

  apply(h: number, action: Action, atMs: number): { ok: boolean; error?: string } {
    const [p, n] = this.put(JSON.stringify(action));
    this.ex.sbl_apply(h, p, n, atMs);
    return this.outJson();
  }

  record(h: number, capture: unknown, atMs: number): void {
    const [p, n] = this.put(JSON.stringify(capture));
    this.ex.sbl_record(h, p, n, atMs);
  }

  step(h: number, dt: number): Frame {
    this.ex.sbl_step(h, dt);
    return this.outJson<Frame>();
  }

  settle(h: number): Frame {
    this.ex.sbl_settle(h);
    return this.outJson<Frame>();
  }

  view(h: number): Frame {
    this.ex.sbl_view(h);
    return this.outJson<Frame>();
  }

  newActivity(activityId: string, seed: number | string, atMs: number): number {
    const [p, n] = this.put(JSON.stringify({ activityId, seed, atMs }));
    const h = this.ex.sbl_activity_new(p, n);
    if (h < 0) throw new Error(this.outJson<{ error: string }>().error);
    return h;
  }

  catalog(): Catalog {
    this.ex.sbl_catalog();
    return this.outJson<Catalog>();
  }

  recordReading(h: number, atMs: number): RecordOutcome {
    this.ex.sbl_record_reading(h, atMs);
    return this.outJson<RecordOutcome>();
  }

  recordScope(h: number, atMs: number): RecordOutcome {
    this.ex.sbl_record_scope(h, atMs);
    return this.outJson<RecordOutcome>();
  }

  predict(h: number, goal: string, value: number, atMs: number): { ok: boolean; correct?: boolean; error?: string } {
    const [p, n] = this.put(JSON.stringify({ goal, value }));
    this.ex.sbl_predict(h, p, n, atMs);
    return this.outJson();
  }

  hint(h: number, atMs: number): HintView | null {
    this.ex.sbl_hint(h, atMs);
    return this.outJson<HintView | null>();
  }

  layout(h: number): Layout {
    this.ex.sbl_layout(h);
    return this.outJson<Layout>();
  }

  log(h: number): LogEntry[] {
    this.ex.sbl_log(h);
    return this.outJson<LogEntry[]>();
  }

  /** Raw output text of an engine trace, for byte-for-byte comparison. */
  engineTraceText(input: unknown): string {
    const [p, n] = this.put(JSON.stringify(input));
    this.ex.sbl_engine_trace(p, n);
    return this.out();
  }
}
